import React, {
  FC,
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslate } from "../../utils/hooks/useTranslate";
import { useLocation, useNavigate } from "react-router-dom";
import Icon from "../Icon/Icon";
import { IconColorE } from "../../models/enums";
import { SelectOptionT } from "../../models/models";
import useQueryParams from "../../utils/hooks/useQueryParams";
import { useWindowResize } from "../../utils/hooks/useWindowResize";
import { categoryOptions } from "../../utils/helpers/categoryOptions";
import { useAppSelector } from "../../store/hooks";
import { selectCurrentUser } from "../../store/selectors/authSelectors";
import SearchDesktop from "./SearchDesktop";
import FloatingSearch from "../FloatingSearch/FloatingSearch";
import SearchCategorySelector from "./SearchCategorySelector";
import { clss } from "../../utils/helpers/styleHelpers";

import styles from "./search.module.scss";
import { getDefaultSearchCategory } from "./helpers/getDefaultSearchCategory";
import getLastValue from "./helpers/getLastValue";
import { RoutesE } from "../../routes/routesData";
import AutocompleteSearch from "../AutocompleteSearch/AutocompleteSearch";
import useApp from "../../utils/hooks/useApp";
import { useNonDesktopViewport } from "../../utils/hooks/useNonDesktopViewport";

/**
 * Search form with search category select
 * Uses query params
 * */

type SearchPropTypes = {
  formID: string;
  refetch?: () => void;
  isCampaignList?: boolean;
  withoutRedirect?: boolean;
  withAutocomplete?: boolean;
};

const Search: FC<SearchPropTypes> = memo(
  (props) => {
    const {
      formID,
      refetch,
      isCampaignList,
      withoutRedirect,
      withAutocomplete,
    } = props;

    // get access to the routing and query params
    const navigate = useNavigate();
    const location = useLocation();
    const { searchParams, addQueryParam } = useQueryParams();

    // input fields control
    const [searchText, setSearchText] = useState("");
    const { control, handleSubmit } = useForm();
    const user = useAppSelector(selectCurrentUser);
    const searchCategoryOptions = useMemo(
      () => categoryOptions(user.role),
      [user.role]
    );
    const [categoryValue, setCategoryValue] = useState<SelectOptionT>(
      searchCategoryOptions.find(
        (option) => option.value === getDefaultSearchCategory()
      ) || searchCategoryOptions[0]
    );
    const [isEditing, setIsEditing] = useState(false);
    const isAdminPage = location.pathname.startsWith("/admin/");
    /** Matches SCSS breakpoints: floating search on tablet + mobile (max-width 991px). */
    const isNonDesktop = useNonDesktopViewport();
    const isDesktop = !isNonDesktop;

    const [showSearchBar, setShowSearchBar] = useState(
      () =>
        isAdminPage ||
        (typeof window !== "undefined" &&
          window.matchMedia("(min-width: 992px)").matches)
    );

    useEffect(() => {
      if (!isDesktop) setShowSearchBar(false);
    }, [isDesktop]);

    useEffect(() => {
      const categoryFilter = searchParams.get("filterType");
      setSearchText((searchParams.get("filter") as string) || "");

      if (categoryFilter) {
        setCategoryValue(
          (searchCategoryOptions.find(
            ({ value }) => value === categoryFilter
          ) as any) || searchCategoryOptions[0]
        );
      }
    }, [searchParams.get("filter"), searchParams.get("filterType")]);

    // get search input ref for focus
    const searchInputRef = useRef<HTMLInputElement>(null);

    // get app global data
    const { t } = useTranslate();
    const { isRtlDirection } = useApp();

    // set default values of the form from query params
    useEffect(() => {
      // get values from query params
      const categoryQueryParam = searchParams.get("filterType");
      const searchTextQueryParam = searchParams.get("filter");

      // set filter type value if the query param exists
      if (categoryQueryParam) {
        const chosenCategoryOptionIndex = searchCategoryOptions.findIndex(
          ({ value }) => value === categoryQueryParam
        );
        setCategoryValue(searchCategoryOptions[chosenCategoryOptionIndex]);
      }
      const lastValue = getLastValue(location.pathname === RoutesE.CAMPAIGNS);
      if (lastValue) {
        const chosenCategoryOptionIndex = searchCategoryOptions.findIndex(
          ({ value }) => value === lastValue
        );
        setCategoryValue(searchCategoryOptions[chosenCategoryOptionIndex]);
      }

      // set filter text value if the query param exists
      if (searchTextQueryParam) {
        setSearchText(searchTextQueryParam);
      }
    }, []);

    // write search request data to query param
    const onSearch = () => {
      if (searchText && categoryValue.value) {
        if (withoutRedirect) {
          addQueryParam({
            filterType: categoryValue.value,
            filter: searchText.trim(),
          });
        } else {
          navigate(
            `/searchresults?sorting=${searchParams.get("sorting")}&filterType=${
              categoryValue.value
            }&filter=${searchText.trim()}`
          );
        }
        refetch && refetch();
        if (!isDesktop) setShowSearchBar(false);
      }
    };

    // clear search fields and query params
    const handleClear = () => {
      const orderParam = searchParams.get("sorting");
      navigate({
        search: orderParam ? `?sorting=${orderParam}` : "",
      });

      // set values to the default and get focus on input
      // setCategoryValue(searchCategoryOptions[isCampaignList ? 4 : 5]);
      refetch && refetch();
      setSearchText((prev) => {
        // На admin страницах не скрываем поиск
        // if field is already empty - hide search bar instead of clearing
        if (!prev && !isAdminPage && !isDesktop) {
          setShowSearchBar(false);
        }

        if (location.pathname.includes("searchresults")) {
          navigate(isCampaignList ? RoutesE.CAMPAIGNS : RoutesE.DELIVERIES);
        }
        return "";
      });
      searchInputRef.current?.focus();
    };

    const searchContent = withAutocomplete ? (
      <AutocompleteSearch
        formID={formID}
        isForCampaignsPage={isCampaignList}
        refetch={refetch}
        withoutRedirect={withoutRedirect}
        setShowSearchBar={isDesktop ? setShowSearchBar : undefined}
      />
    ) : (
      <form
        id={formID}
        onSubmit={handleSubmit(onSearch)}
        className={styles.wrapper}
      >
        <SearchCategorySelector
          control={control}
          value={categoryValue}
          onChange={setCategoryValue}
          isCampaignList={isCampaignList}
        />
        <Controller
          name="text"
          control={control}
          render={({ field }) => (
            <div className={styles.searchInputWrapper}>
              <input
                {...field}
                ref={searchInputRef}
                className={clss([
                  styles.searchInput,
                  isEditing ? styles.editingSearchInput : "",
                ])}
                placeholder={
                  isCampaignList
                    ? t`Enter request to search for campaigns`
                    : t`Enter request to search for deliveries`
                }
                autoComplete="off"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onFocus={() => setIsEditing(true)}
                onBlur={() => setIsEditing(false)}
              />
              <div
                className={
                  isRtlDirection
                    ? styles.clearIconWrapperRtl
                    : styles.clearIconWrapper
                }
              >
                <Icon
                  name="close"
                  className={styles.clearIcon}
                  color={IconColorE.GRAY}
                  onClick={handleClear}
                  title="Clear"
                />
              </div>
            </div>
          )}
        />
        <button type="submit" className={styles.submitButton}>
          <Icon
            name="search"
            color={isDesktop ? IconColorE.WHITE : IconColorE.BLUE}
            title="Search"
          />
        </button>
      </form>
    );

    return isDesktop ? (
      <SearchDesktop>{searchContent}</SearchDesktop>
    ) : (
      <FloatingSearch
        showSearchBar={showSearchBar}
        setShowSearchBar={setShowSearchBar}
        isForCampaignsPage={isCampaignList}
        refetch={refetch}
        withoutRedirect={withoutRedirect}
      />
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.formID === nextProps.formID &&
      prevProps.isCampaignList === nextProps.isCampaignList
    );
  }
);

export default Search;
